import React from 'react'
import {
  AlertTriangle,
  Brain,
  Check,
  Database,
  RefreshCw,
  Search,
  Sparkles,
} from 'lucide-react'
import { StatusUpdate } from '../../api/chatService'

interface StatusTrailProps {
  steps: StatusUpdate[]
}

const KIND_ICONS: Record<StatusUpdate['kind'], React.ComponentType<{ size?: number }>> =
  {
    agent: Sparkles,
    tool: Database,
    search: Search,
    thinking: Brain,
    retry: RefreshCw,
  }

/**
 * The live "what is BreadGPT doing" trail.
 *
 * Replaces the bare three-dot spinner that used to be the only sign of life for
 * the whole of a turn — including the minutes an analytics question can spend
 * running DAX before a single token of the answer exists. Failed steps stay in
 * the list as warnings rather than disappearing, because a query that failed and
 * was retried is exactly the sort of thing that explains a thin answer.
 */
const StatusTrail: React.FC<StatusTrailProps> = ({ steps }) => {
  if (steps.length === 0) return null

  return (
    <ol className="status-trail" aria-live="polite" aria-label="Progress">
      {steps.map(step => {
        const Icon =
          step.state === 'warning'
            ? AlertTriangle
            : step.state === 'done'
              ? Check
              : KIND_ICONS[step.kind] || Sparkles

        return (
          <li
            key={step.id}
            className={`status-trail__step status-trail__step--${step.state}`}
          >
            <span className="status-trail__icon">
              <Icon size={14} />
            </span>
            <span className="status-trail__body">
              <span className="status-trail__label">{step.label}</span>
              {step.detail && (
                <span className="status-trail__detail">{step.detail}</span>
              )}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

export default StatusTrail
